(() => {
  const node = document.querySelector('[data-renly-checkout-catalog]');
  if (!node) return;
  const catalog = JSON.parse(node.textContent);
  const target = new Map(catalog.variants.map(variant => [variant.sku, Number(variant.id)]));
  const family = new Map(catalog.family.map(variant => [Number(variant.id), variant]));
  const productIds = new Set(catalog.family.map(variant => Number(variant.productId)));
  const base = window.Shopify?.routes?.root || '/';
  let pending;
  async function request(name, body) {
    const response = await fetch(`${base}cart${name ? `/${name}` : ''}.js`, {
      method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || window.RenlyUI?.cart_update_error || 'Please try again.');
    return result;
  }
  const isGift = item => Number(item.variant_id) === catalog.giftVariantId || Number(item.product_id) === catalog.giftProductId || Number(item.variant_id) === catalog.valuedGiftVariantId || Number(item.product_id) === catalog.valuedGiftProductId;
  const isValuedGift = item => !!catalog.valuedGiftVariantId && Number(item.variant_id) === catalog.valuedGiftVariantId;
  const valuedGiftEligible = cart => {
    const cleaner = cart.items.filter(item => !isGift(item) && productIds.has(Number(item.product_id)) && item.final_line_price > 0);
    return !!catalog.valuedGiftVariantId && cleaner.length > 0;
  };
  const giftVariantFor = cart => catalog.valuedGiftVariantId && valuedGiftEligible(cart) ? catalog.valuedGiftVariantId : catalog.giftVariantId;
  const giftCount = cart => valuedGiftEligible(cart) ? 1 : new Set(cart.items.filter(item => !isGift(item) && item.final_line_price > 0 && !(item.properties?._ftb_experiment === 'renly-se-cleaner-mystery-gift-v1' && item.properties?._ftb_variant === 'test')).map(item => productIds.has(Number(item.product_id)) ? 'cleaner' : String(item.product_id))).size;
  async function syncGifts(cart) {
    if (!catalog.giftVariantId) return cart;
    const count = giftCount(cart);
    let gifts = cart.items.filter(isGift);
    const unsafe = gifts.filter(item => isValuedGift(item) && (item.final_line_price !== 0 || !valuedGiftEligible(cart)));
    if (unsafe.length) {
      cart = await request('update', {updates:Object.fromEntries(unsafe.map(item=>[item.key,0]))});
      if (unsafe.some(item=>item.final_line_price !== 0 && valuedGiftEligible(cart))) throw new Error(window.RenlyUI?.gift_error || 'The free gift could not be confirmed. Please try again.');
      gifts = cart.items.filter(isGift);
    }
    // Confirm the new native free gift before removing a legacy zero-price gift.
    // A failed/ambiguous add must retain the original gift and all paid lines.
    if (valuedGiftEligible(cart) && gifts.some(item => !isValuedGift(item))) {
      if (!gifts.some(isValuedGift)) {
        await request('add', {items:[{id:catalog.valuedGiftVariantId,quantity:1,properties:{_renly_gift:'true'}}]});
        cart = await request('');
      }
      const replacement = cart.items.filter(isValuedGift);
      if (replacement.length !== 1 || replacement[0].quantity !== 1 || replacement[0].final_line_price !== 0) {
        if (replacement.length) await request('update', {updates:Object.fromEntries(replacement.map(item=>[item.key,0]))});
        throw new Error(window.RenlyUI?.gift_error || 'The free gift could not be confirmed. Please try again.');
      }
      const legacy = cart.items.filter(item => isGift(item) && !isValuedGift(item));
      if (legacy.length) cart = await request('update', {updates:Object.fromEntries(legacy.map(item=>[item.key,0]))});
      gifts = cart.items.filter(isGift);
    }
    if (gifts.length === 1 && gifts[0].quantity === count) return cart;
    if (!gifts.length && !count) return cart;
    const updates = Object.fromEntries(gifts.map(item => [item.key, 0]));
    if (gifts.length) cart = await request('update', { updates });
    if (count) {
      await request('add', { items: [{ id: gifts.some(item => !isValuedGift(item)) ? catalog.giftVariantId : giftVariantFor(cart), quantity: count, properties: { _renly_gift: 'true' } }] });
      cart = await request('');
    }
    return cart;
  }
  const replacementId = item => target.get(family.get(Number(item.variant_id))?.sku);
  const needsMigration = cart => cart.items.some(item => {
    const id = replacementId(item);
    return id && id !== Number(item.variant_id) && !item.selling_plan_allocation;
  });
  async function normalize(cart) {
    if (pending) return pending;
    pending = (async () => {
      // Read again before a migration: another cart read can have overlapped the previous write.
      let current = cart || await request('');
      for (const source of [...current.items]) {
        const id = replacementId(source);
        if (!id || id === Number(source.variant_id) || source.selling_plan_allocation) continue;
        if (!current.items.some(item => item.key === source.key)) continue;
        // Recovery marker prevents duplicate adds if a response is lost after the replacement was added.
        const marker = source.properties?._renly_locale_source || source.key;
        const alreadyAdded = current.items.some(item => Number(item.variant_id) === id && item.properties?._renly_locale_source === marker);
        if (!alreadyAdded) {
          await request('add', { items: [{ id, quantity: source.quantity, properties: { ...source.properties, _renly_locale_source: marker } }] });
        }
        // Keep the original line until the replacement is confirmed; never clear a customer's cart.
        current = await request('change', { id: source.key, quantity: 0 });
      }
      current = await syncGifts(current);
      if (current.items.some(isValuedGift)) {
        current = await request('');
        const bad = current.items.filter(item=>isValuedGift(item) && (item.quantity !== 1 || item.final_line_price !== 0 || !valuedGiftEligible(current)));
        if (bad.length) {
          await request('update',{updates:Object.fromEntries(bad.map(item=>[item.key,0]))});
          throw new Error(window.RenlyUI?.gift_error || 'The free gift could not be confirmed. Please try again.');
        }
      }
      return current;
    })();
    try { return await pending; } finally { pending = null; }
  }
  window.RenlyCheckout = { normalize, needsMigration, family, productIds, catalog, isGift, isValuedGift, valuedGiftEligible, giftVariantFor, giftCount, syncGifts };
  // Native cart page is server-rendered: reload only after an actual language migration.
  if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')  ) {
    const buttons = [...document.querySelectorAll('[name="checkout"]')];
    buttons.forEach(button => { button.disabled = true; });
    normalize(catalog.cart).then(cart => { if (JSON.stringify(cart.items.map(item => [item.key,item.quantity])) !== JSON.stringify(catalog.cart.items.map(item => [item.key,item.quantity]))) window.location.reload(); else buttons.forEach(button => { button.disabled = false; }); }).catch(error => {
      buttons.forEach(button => { button.disabled = false; });
      const message = document.querySelector('#cart-errors');
      if (message) message.textContent = error.message;
    });
  }
  // Dawn checkout paths also normalize a cart created in another storefront language.
  let proceeding = false;
  document.addEventListener('click', async event => {
    const checkout = event.target.closest('a[href$="/checkout"],button[name="checkout"]');
    if (!checkout || checkout.hasAttribute('data-checkout') || proceeding) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    proceeding = true;
    checkout.setAttribute('aria-busy', 'true');
    try { await normalize(); window.location.assign(`${base}checkout`); }
    catch (error) {
      proceeding = false; checkout.removeAttribute('aria-busy');
      const message = document.querySelector('#cart-errors,.cart__error');
      if (message) message.textContent = error.message;
    }
  }, true);
})();
